import { useState, useEffect, useRef } from 'react';
import { getUsers, getProjects, getClients, getContracts, getActivityTypes, getCustomFields, getProjectStatuses, getBusinessUnits } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { MultiSelect } from './ui/multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Card, CardContent } from './ui/card';
import { X, Filter } from 'lucide-react';
import PeriodPresets, { PERIOD_PRESETS } from './PeriodPresets';

const USER_ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'project_manager', label: 'Project Manager' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'venditore', label: 'Operativo' },
  { value: 'account', label: 'Account Manager' },
  { value: 'cliente', label: 'Cliente' },
];

// Helper: comma-separated string ↔ array
const toArr = (v) => (v || '').split(',').filter(Boolean);
const toStr = (arr) => arr.join(',');

export default function DashboardFilters({ filters, onFiltersChange, userRole }) {
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [projectStatuses, setProjectStatuses] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [customFieldFilters, setCustomFieldFilters] = useState([]);
  const [datePreset, setDatePreset] = useState('');
  // Local buffer for custom dates (fix: avoid refresh on first date pick)
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const isClienteRole = userRole === 'cliente';

  useEffect(() => {
    (async () => {
      try {
        const [usersRes, projectsRes, clientsRes, contractsRes, activityTypesRes, projectStatusesRes, businessUnitsRes, customFieldsRes] = await Promise.all([
          getUsers().catch(() => ({ data: [] })),
          getProjects().catch(() => ({ data: [] })),
          getClients().catch(() => ({ data: [] })),
          getContracts().catch(() => ({ data: [] })),
          getActivityTypes().catch(() => ({ data: [] })),
          getProjectStatuses().catch(() => ({ data: [] })),
          getBusinessUnits().catch(() => ({ data: [] })),
          Promise.all([
            getCustomFields('project').catch(() => ({ data: [] })),
            getCustomFields('contract').catch(() => ({ data: [] })),
            getCustomFields('client').catch(() => ({ data: [] })),
          ]).then(([p, c, cl]) => ({
            data: [
              ...p.data.map(f => ({ ...f, entity: 'Progetto' })),
              ...c.data.map(f => ({ ...f, entity: 'Contratto' })),
              ...cl.data.map(f => ({ ...f, entity: 'Cliente' })),
            ]
          })),
        ]);
        setUsers(usersRes.data);
        setProjects(projectsRes.data);
        setClients(clientsRes.data);
        setContracts(contractsRes.data);
        setActivityTypes(activityTypesRes.data);
        setProjectStatuses(projectStatusesRes.data);
        setBusinessUnits(businessUnitsRes.data);
        setCustomFields(customFieldsRes.data);
      } catch (err) {
        console.error('Error loading filter data:', err);
      }
    })();
  }, []);

  // Sync customFieldFilters when filters.custom_fields changes
  useEffect(() => {
    if (filters.custom_fields) {
      try { setCustomFieldFilters(JSON.parse(filters.custom_fields)); } catch { setCustomFieldFilters([]); }
    } else {
      setCustomFieldFilters([]);
    }
  }, [filters.custom_fields]);

  const handleMultiChange = (key, arr) => {
    const val = toStr(arr);
    if (val) {
      onFiltersChange({ ...filters, [key]: val });
    } else {
      const next = { ...filters };
      delete next[key];
      onFiltersChange(next);
    }
  };

  const handleReset = () => {
    setCustomFieldFilters([]);
    setDatePreset('all');
    setCustomStartDate('');
    setCustomEndDate('');
    onFiltersChange({});
  };

  const handleDatePresetChange = (preset) => {
    if (!preset || preset === 'all') {
      setDatePreset('all');
      setCustomStartDate('');
      setCustomEndDate('');
      const next = { ...filters };
      delete next.start_date;
      delete next.end_date;
      onFiltersChange(next);
      return;
    }
    if (preset === 'custom') {
      setDatePreset('custom');
      setCustomStartDate(filters.start_date || '');
      setCustomEndDate(filters.end_date || '');
      return;
    }
    const p = PERIOD_PRESETS.find(d => d.value === preset);
    if (p) {
      const { start, end } = p.getRange();
      setDatePreset(preset);
      setCustomStartDate('');
      setCustomEndDate('');
      onFiltersChange({ ...filters, start_date: start, end_date: end });
    }
  };

  // Custom date handlers: only propagate when BOTH dates are filled
  const handleCustomStart = (v) => {
    setCustomStartDate(v);
    if (v && customEndDate) {
      onFiltersChange({ ...filters, start_date: v, end_date: customEndDate });
    }
  };
  const handleCustomEnd = (v) => {
    setCustomEndDate(v);
    if (customStartDate && v) {
      onFiltersChange({ ...filters, start_date: customStartDate, end_date: v });
    }
  };

  // Custom fields
  const addCustomFieldFilter = () => setCustomFieldFilters([...customFieldFilters, { field_id: '', value: '' }]);

  const updateCustomFieldFilter = (index, key, value) => {
    const updated = [...customFieldFilters];
    updated[index][key] = value;
    setCustomFieldFilters(updated);
    const valid = updated.filter(f => f.field_id && f.value);
    const newVal = valid.length > 0 ? JSON.stringify(valid) : '';
    if (newVal !== (filters.custom_fields || '')) {
      const next = { ...filters };
      if (valid.length > 0) { next.custom_fields = newVal; } else { delete next.custom_fields; }
      onFiltersChange(next);
    }
  };

  const removeCustomFieldFilter = (index) => {
    const updated = customFieldFilters.filter((_, i) => i !== index);
    setCustomFieldFilters(updated);
    const valid = updated.filter(f => f.field_id && f.value);
    const next = { ...filters };
    if (valid.length > 0) { next.custom_fields = JSON.stringify(valid); } else { delete next.custom_fields; }
    onFiltersChange(next);
  };

  const removeFilter = (key) => {
    const next = { ...filters };
    delete next[key];
    onFiltersChange(next);
  };

  const getActiveFiltersCount = () => {
    let count = Object.keys(filters).filter(k => k !== 'custom_fields' && filters[k]).length;
    count += customFieldFilters.filter(f => f.field_id && f.value).length;
    return count;
  };

  const activeCount = getActiveFiltersCount();

  // Options for MultiSelect
  const roleOptions = USER_ROLES;
  const userOptions = users.map(u => ({ value: u.id, label: `${u.name} (${u.email})` }));
  const clientOptions = clients.map(c => ({ value: c.id, label: c.company_name }));
  const projectOptions = projects.map(p => ({ value: p.id, label: p.name }));
  const contractOptions = contracts.map(c => ({ value: c.id, label: c.name }));
  const activityTypeOptions = activityTypes.map(t => ({ value: t.id, label: t.name }));
  const projectStatusOptions = projectStatuses.map(s => ({ value: s.value, label: s.label }));
  const businessUnitOptions = businessUnits.map(b => ({ value: b.id, label: b.name }));

  // Helper to get display labels for active filter badges
  const getFilterBadges = () => {
    const badges = [];
    const multiFilters = [
      { key: 'role', label: 'Ruolo', opts: USER_ROLES },
      { key: 'user_id', label: 'Utente', opts: userOptions },
      { key: 'client_id', label: 'Cliente', opts: clientOptions },
      { key: 'project_id', label: 'Progetto', opts: projectOptions },
      { key: 'contract_id', label: 'Contratto', opts: contractOptions },
      { key: 'activity_type_id', label: 'Tipo Attività', opts: activityTypeOptions },
      { key: 'project_status', label: 'Stato Progetto', opts: projectStatusOptions },
      { key: 'business_unit_id', label: 'Business Unit', opts: businessUnitOptions },
    ];
    for (const mf of multiFilters) {
      const arr = toArr(filters[mf.key]);
      if (arr.length > 0) {
        const names = arr.map(v => mf.opts.find(o => o.value === v)?.label || v);
        badges.push({ key: mf.key, label: mf.label, display: names.join(', ') });
      }
    }
    if (filters.start_date) badges.push({ key: 'start_date', label: 'Da', display: filters.start_date });
    if (filters.end_date) badges.push({ key: 'end_date', label: 'A', display: filters.end_date });
    return badges;
  };

  return (
    <Card data-testid="dashboard-filters">
      <CardContent className="pt-6">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-600" />
              <h3 className="font-semibold text-gray-900">Filtri Dashboard</h3>
              {activeCount > 0 && <Badge variant="secondary">{activeCount} attivi</Badge>}
            </div>
            {activeCount > 0 && (
              <Button variant="outline" size="sm" onClick={handleReset}>
                <X className="w-4 h-4 mr-1" /> Reset
              </Button>
            )}
          </div>

          {/* Period presets pills */}
          <PeriodPresets
            activePreset={datePreset || 'all'}
            onPresetChange={handleDatePresetChange}
            showCustomDates={datePreset === 'custom'}
            startDate={customStartDate}
            endDate={customEndDate}
            onStartDateChange={handleCustomStart}
            onEndDateChange={handleCustomEnd}
            testIdPrefix="dashboard-period"
          />

          {/* Filters Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Ruolo */}
            {!isClienteRole && (
              <div className="space-y-2">
                <Label>Ruolo</Label>
                <MultiSelect
                  options={roleOptions}
                  selected={toArr(filters.role)}
                  onChange={(arr) => handleMultiChange('role', arr)}
                  placeholder="Tutti i Ruoli"
                  searchPlaceholder="Cerca ruolo..."
                  data-testid="filter-role"
                />
              </div>
            )}

            {/* Utente */}
            <div className="space-y-2">
              <Label>Utente</Label>
              <MultiSelect
                options={userOptions}
                selected={toArr(filters.user_id)}
                onChange={(arr) => handleMultiChange('user_id', arr)}
                placeholder="Tutti gli utenti"
                searchPlaceholder="Cerca utente..."
                data-testid="filter-user"
              />
            </div>

            {/* Cliente */}
            {!isClienteRole && (
              <div className="space-y-2">
                <Label>Cliente</Label>
                <MultiSelect
                  options={clientOptions}
                  selected={toArr(filters.client_id)}
                  onChange={(arr) => handleMultiChange('client_id', arr)}
                  placeholder="Tutti i clienti"
                  searchPlaceholder="Cerca cliente..."
                  data-testid="filter-client"
                />
              </div>
            )}

            {/* Progetto */}
            <div className="space-y-2">
              <Label>Progetto</Label>
              <MultiSelect
                options={projectOptions}
                selected={toArr(filters.project_id)}
                onChange={(arr) => handleMultiChange('project_id', arr)}
                placeholder="Tutti i progetti"
                searchPlaceholder="Cerca progetto..."
                data-testid="filter-project"
              />
            </div>

            {/* Contratto */}
            <div className="space-y-2">
              <Label>Contratto</Label>
              <MultiSelect
                options={contractOptions}
                selected={toArr(filters.contract_id)}
                onChange={(arr) => handleMultiChange('contract_id', arr)}
                placeholder="Tutti i contratti"
                searchPlaceholder="Cerca contratto..."
                data-testid="filter-contract"
              />
            </div>

            {/* Tipo Attività */}
            <div className="space-y-2">
              <Label>Tipo Attività</Label>
              <MultiSelect
                options={activityTypeOptions}
                selected={toArr(filters.activity_type_id)}
                onChange={(arr) => handleMultiChange('activity_type_id', arr)}
                placeholder="Tutti i tipi"
                searchPlaceholder="Cerca tipo..."
                data-testid="filter-activity-type"
              />
            </div>

            {/* Stato Progetto */}
            <div className="space-y-2">
              <Label>Stato Progetto</Label>
              <MultiSelect
                options={projectStatusOptions}
                selected={toArr(filters.project_status)}
                onChange={(arr) => handleMultiChange('project_status', arr)}
                placeholder="Tutti gli stati"
                searchPlaceholder="Cerca stato..."
                data-testid="filter-project-status"
              />
            </div>

            {/* Business Unit */}
            {businessUnitOptions.length > 0 && (
              <div className="space-y-2">
                <Label>Business Unit</Label>
                <MultiSelect
                  options={businessUnitOptions}
                  selected={toArr(filters.business_unit_id)}
                  onChange={(arr) => handleMultiChange('business_unit_id', arr)}
                  placeholder="Tutte le Business Unit"
                  searchPlaceholder="Cerca business unit..."
                  data-testid="filter-business-unit"
                />
              </div>
            )}

          </div>

          {/* Custom Fields Section */}
          {!isClienteRole && customFields.length > 0 && (
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-4">
                <Label className="text-base font-semibold">Filtri Campi Personalizzati</Label>
                <Button type="button" variant="outline" size="sm" onClick={addCustomFieldFilter}>
                  + Aggiungi Campo
                </Button>
              </div>
              {customFieldFilters.length === 0 && (
                <p className="text-sm text-gray-500 italic">Nessun filtro per campi personalizzati. Clicca "+ Aggiungi Campo" per aggiungerne uno.</p>
              )}
              <div className="space-y-3">
                {customFieldFilters.map((filter, index) => {
                  const selectedField = customFields.find(f => f.id === filter.field_id);
                  return (
                    <div key={index} className="flex gap-3 items-end border rounded-lg p-3 bg-gray-50">
                      <div className="flex-1 space-y-2">
                        <Label className="text-xs">Campo</Label>
                        <Select value={filter.field_id || 'none'} onValueChange={(v) => updateCustomFieldFilter(index, 'field_id', v === 'none' ? '' : v)}>
                          <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Seleziona campo</SelectItem>
                            {customFields.map(f => (
                              <SelectItem key={f.id} value={f.id}>{f.name} ({f.entity})</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex-1 space-y-2">
                        <Label className="text-xs">Valore</Label>
                        {!selectedField && <Input disabled placeholder="Seleziona prima un campo" />}
                        {selectedField && selectedField.field_type === 'text' && (
                          <Input type="text" value={filter.value || ''} onChange={(e) => updateCustomFieldFilter(index, 'value', e.target.value)} placeholder="Inserisci valore..." />
                        )}
                        {selectedField && selectedField.field_type === 'number' && (
                          <Input type="number" value={filter.value || ''} onChange={(e) => updateCustomFieldFilter(index, 'value', e.target.value)} placeholder="Inserisci numero..." />
                        )}
                        {selectedField && selectedField.field_type === 'select' && (
                          <Select value={filter.value || 'none'} onValueChange={(v) => updateCustomFieldFilter(index, 'value', v === 'none' ? '' : v)}>
                            <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Seleziona valore</SelectItem>
                              {selectedField.options?.map(opt => (
                                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {selectedField && selectedField.field_type === 'boolean' && (
                          <Select value={filter.value || 'none'} onValueChange={(v) => updateCustomFieldFilter(index, 'value', v === 'none' ? '' : v)}>
                            <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Seleziona valore</SelectItem>
                              <SelectItem value="true">Si</SelectItem>
                              <SelectItem value="false">No</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeCustomFieldFilter(index)} className="mb-0.5">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Active Filters Badges */}
          {activeCount > 0 && (
            <div className="flex flex-wrap gap-2 pt-2 border-t">
              {getFilterBadges().map(b => (
                <Badge key={b.key} variant="secondary" className="gap-1">
                  {b.label}: {b.display}
                  <button onClick={() => removeFilter(b.key)} className="ml-1 hover:bg-gray-300 rounded-full p-0.5">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
              {customFieldFilters.filter(f => f.field_id && f.value).map((cf, index) => {
                const field = customFields.find(f => f.id === cf.field_id);
                return (
                  <Badge key={`cf-${index}`} variant="secondary" className="gap-1">
                    {field?.name || 'Campo'}: {cf.value}
                    <button onClick={() => removeCustomFieldFilter(index)} className="ml-1 hover:bg-gray-300 rounded-full p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
